import React from 'react';
import { observer } from 'mobx-react';

import { LinearProgress } from '@mui/material';

import { Button } from 'components/Button';
import { CenteredCircularProgress } from 'components/Progress';

import { ActivityType } from 'utils/stores/Activities/constants';

import { FileUploadProgressResponse } from 'ui/types';

import { EntityModules, ProtectorType } from 'core/constants';

import { AttachmentType } from 'utils/api/modules/incidents/incidents.types';

import EventDetailsStore from 'stores/EventDetails';

import { AuditHostKind } from 'stores/EntitiesActivities/EntitiesActivities';

import { EventDetailsType } from 'stores/EventDetails/EventDetails';

import { getFieldKeysBasedOnProtectorType } from 'stores/EventDetails/helpers';

import { UuidableName } from 'utils/api/types';

import { UserNameResponse } from 'utils/api/modules/user/user.types';

import { BackgroundInformationSections } from '../EntityDetailsTab/styles';
import { EntitiesStores } from '../types';

import { AttachmentsPanel, EmptyState } from './styles';

import FileContainer from './components/FileContainer';
import FileUploadProgress from './components/FileUploadProgress';
import UploadAttachment from './components/UploadAttachment';

const EntityAttachmentTab: React.FunctionComponent<{ store: EntitiesStores }> = ({ store }) => {
  const { activities, details } = store;

  const [submissionAttachments, setSubmissionAttachments] = React.useState<AttachmentType[]>([]);

  const eventReporter = React.useMemo(() => {
    if (store instanceof EventDetailsStore) {
      if (store.details?.entity[`${store.prefix}reporter`]?.value) {
        return store.details?.entity[`${store.prefix}reporter`]?.value as UuidableName;
      }
      return { name: 'N/A', uuid: 'N/A' } as UuidableName;
    }
    return null;
  }, [store]);

  // A migrated audit anchors its files on the legacy submission, which the endpoint also accepts;
  // an in-progress one anchors on its draft. A scheduled audit never opened has neither yet.
  const auditDetails = store.details as
    | { submission?: unknown; legacySubmission?: unknown; draft?: { uuid: string } | null }
    | undefined;
  let auditHostKind: AuditHostKind | null = null;
  let auditDraftUuid: string | null = null;
  if (auditDetails?.submission || auditDetails?.legacySubmission) {
    auditHostKind = 'submission';
  } else if (auditDetails?.draft?.uuid) {
    auditHostKind = 'draft';
    auditDraftUuid = auditDetails.draft.uuid;
  }

  React.useEffect(() => {
    activities?.loadAttachments(false, auditHostKind, auditDraftUuid);
  }, [activities, auditHostKind, auditDraftUuid]);

  React.useEffect(() => {
    if (store instanceof EventDetailsStore) {
      const { submission } = store.details as EventDetailsType;
      const attachmentKeys = getFieldKeysBasedOnProtectorType(submission, ProtectorType.Attachment);

      const allAttachments: AttachmentType[] = [];
      attachmentKeys.forEach((key) => {
        const value = submission.content[key];
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'filename' in value[0]) {
          allAttachments.push(...(value as AttachmentType[]));
        }
      });

      setSubmissionAttachments(allAttachments);
    }
  }, [store]);

  if (activities?.loadingAttachments) {
    return (
      <BackgroundInformationSections>
        <CenteredCircularProgress CircularProgressProps={{ style: { margin: '20px 0' } }} />
      </BackgroundInformationSections>
    );
  }

  const onDeleteFile = (uuid: string): void => {
    // for events check if the file is coming form additional comments
    if (store.entityType === EntityModules.Events) {
      // check if the file is in activities
      const isSubmission = activities?.attachments.find((attachment) => attachment.uuid === uuid)?.isSubmission;
      if (isSubmission) {
        // delete the file from the activities
        (store as EventDetailsStore).reporterActivities?.deleteFile(uuid);
        return;
      }
    }

    activities?.deleteFile(uuid);
  };

  const isAudit = store.entityType === EntityModules.Audits;
  // The audit record has no outer card, so the tab paints the white panel the legacy record's did.
  const Panel = isAudit ? AttachmentsPanel : BackgroundInformationSections;
  // Without a host there is nothing to upload against, so the control would only 404.
  const canUploadToAudit = isAudit && auditHostKind !== null;
  // `attachmentsLoaded` gates this: a swallowed load failure must not read as "no files".
  const isEmptyAudit = isAudit && activities?.attachmentsLoaded && !activities.attachments.length;

  return (
    <Panel data-testid={isAudit ? 'audit-attachments-tab' : undefined}>
      {(!isAudit || canUploadToAudit) && <UploadAttachment uploadFile={activities?.uploadFile} multiple />}
      {isEmptyAudit && (
        <EmptyState data-testid="audit-attachments-empty">
          {canUploadToAudit
            ? 'No attachments yet. Files added here belong to the whole audit; files added against a question appear here too.'
            : 'Attachments are added against individual questions.'}
        </EmptyState>
      )}
      {activities?.deletingFile && <LinearProgress />}

      {submissionAttachments.map((attachment) => (
        <FileContainer
          key={attachment.filename}
          attachment={{
            filename: attachment.filename,
            size: attachment.size,
            type: ActivityType.File,
            file: attachment.url,
            uuid: attachment.filename,
            created: details?.createdAt || null,
            creator: eventReporter as UserNameResponse,
          }}
          deleteDisabledReason="This attachment was added during form submission and cannot be removed."
        />
      ))}

      {activities?.attachments.map((attachment) =>
        // A question's file is owned by that question's panel; a record-level one (no questionId)
        // belongs to the audit and is removed here.
        isAudit && attachment.questionId ? (
          <FileContainer
            key={attachment.uuid}
            attachment={attachment}
            deleteDisabledReason="This attachment was added against a question and is removed from the question panel."
          />
        ) : (
          <FileContainer key={attachment.uuid} attachment={attachment} deleteFile={onDeleteFile} />
        )
      )}

      {activities?.activities
        ?.filter((activity) => activity.type === ActivityType.FileUploadProgress)
        .map((activity) => (
          <FileUploadProgress fileActivity={activity as FileUploadProgressResponse} />
        ))}

      {activities?.loadingMoreAttachments && (
        <CenteredCircularProgress CircularProgressProps={{ style: { margin: '20px 0' } }} />
      )}

      {activities?.attachmentsHasMore && !activities?.loadingMoreAttachments && (
        <Button variant="outlined" fullWidth size="large" onClick={() => activities?.loadAttachments(true)}>
          Load more
        </Button>
      )}
    </Panel>
  );
};

export default observer(EntityAttachmentTab);
